<template>
  <div style="max-width: 90%; margin: auto" class="py-10">
    <!-- Header -->
    <div class="mb-8">
      <p class="page-subtitle mb-1">Verwaltung</p>
      <h1 class="page-title">Einstellungen</h1>
    </div>

    <!-- Section: Shops -->
    <div class="section-head mb-5">
      <div>
        <div class="d-flex align-center" style="gap: 10px">
          <div class="section-icon"><v-icon size="16" color="#5B5BD6">mdi-store-outline</v-icon></div>
          <h2 class="section-title">Onlineshops</h2>
        </div>
        <p class="section-desc mt-1">Shops mit ABLyft-Projekt verknüpfen</p>
      </div>
      <button class="primary-btn" @click="openShopDialog()">
        <v-icon size="16">mdi-plus</v-icon>
        <span>Neuer Shop</span>
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loadingShops" class="skel-grid">
      <div v-for="i in 4" :key="`skel-${i}`" class="skel-card"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="shops.length === 0" class="empty-state">
      <v-icon size="48" color="#c5cbd3" class="mb-4">mdi-store-outline</v-icon>
      <h3 class="empty-title">Keine Shops vorhanden</h3>
      <p class="empty-desc">Lege einen Onlineshop an, um ABLyft-Projekte zu verknüpfen.</p>
      <button class="primary-btn mt-4" @click="openShopDialog()">
        <v-icon size="16">mdi-plus</v-icon>
        Ersten Shop anlegen
      </button>
    </div>

    <!-- Shops Grid -->
    <div v-else class="shops-grid">
      <div v-for="shop in shops" :key="shop.id" class="shop-card">
        <div class="shop-head">
          <div class="shop-icon-badge">
            <v-icon size="16" color="#0a1628">mdi-store-outline</v-icon>
          </div>
          <div class="shop-name-wrap">
            <div class="shop-name">{{ shop.name }}</div>
          </div>
          <div class="shop-actions">
            <button class="action-btn" title="Bearbeiten" @click="openShopDialog(shop)">
              <v-icon size="16">mdi-pencil-outline</v-icon>
            </button>
            <button class="action-btn action-danger" title="Löschen" @click="confirmDeleteShop(shop)">
              <v-icon size="16">mdi-delete-outline</v-icon>
            </button>
          </div>
        </div>

        <div class="shop-body">
          <div v-if="shop.ablyftProjectId" class="shop-meta">
            <v-icon size="13" color="#5B5BD6">mdi-flask-outline</v-icon>
            <span class="meta-label">ABLyft Projekt:</span>
            <a :href="ablyftUrl(shop.ablyftProjectId)" target="_blank" class="meta-link">
              {{ shop.ablyftProjectId }}
              <v-icon size="11">mdi-open-in-new</v-icon>
            </a>
          </div>
          <div v-else class="shop-meta shop-meta-empty">
            <v-icon size="13" color="#c5cbd3">mdi-flask-off-outline</v-icon>
            <span>Kein ABLyft-Projekt verknüpft</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Shop Create/Edit Dialog -->
    <div v-if="shopDialog" class="dialog-backdrop" @click="!savingShop && (shopDialog = false)"></div>
    <v-dialog v-model="shopDialog" max-width="520" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" :disabled="savingShop" @click="shopDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon"><v-icon size="22" color="#0a1628">{{ editShop ? 'mdi-pencil-outline' : 'mdi-store-outline' }}</v-icon></div>
          <div>
            <div class="dh-title">{{ editShop ? 'Shop bearbeiten' : 'Neuen Shop anlegen' }}</div>
            <div class="dh-sub">Verknüpfe den Shop mit einer ABLyft-Projekt-ID</div>
          </div>
        </div>
        <div class="db">
          <div class="fg mb-3">
            <label class="fl">Shop-Name *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'name' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-store-outline</v-icon>
              <input
                v-model="shopForm.name"
                class="ii"
                placeholder="z.B. mein-shop.myshopify.com"
                :disabled="savingShop"
                @focus="focusField = 'name'"
                @blur="focusField = null"
              />
            </div>
          </div>

          <div class="fg">
            <label class="fl">ABLyft Projekt-ID *</label>
            <div class="iw" :class="{ 'iw-f': focusField === 'projectId' }">
              <v-icon size="15" color="rgba(0,0,0,0.3)">mdi-flask-outline</v-icon>
              <input
                v-model="shopForm.ablyftProjectId"
                class="ii"
                placeholder="z.B. 14122976"
                :disabled="savingShop"
                @focus="focusField = 'projectId'"
                @blur="focusField = null"
              />
            </div>
            <div v-if="shopForm.ablyftProjectId" class="hint mt-2">
              <v-icon size="12">mdi-link-variant</v-icon>
              <a :href="ablyftUrl(shopForm.ablyftProjectId)" target="_blank">{{ ablyftUrl(shopForm.ablyftProjectId) }}</a>
            </div>
          </div>

          <div v-if="shopMessage" class="mt-4" :class="shopMessageType === 'error' ? 'alert-error' : 'alert-success'">
            <v-icon size="16" :color="shopMessageType === 'error' ? '#D32F2F' : '#2E7D32'">
              {{ shopMessageType === 'error' ? 'mdi-alert-circle-outline' : 'mdi-check-circle-outline' }}
            </v-icon>
            {{ shopMessage }}
          </div>
        </div>
        <div class="dff">
          <button class="bo" :disabled="savingShop" @click="shopDialog = false">Abbrechen</button>
          <button class="bp bp-success" :disabled="savingShop" @click="saveShop">
            <v-progress-circular v-if="savingShop" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-check</v-icon>
            <span>{{ editShop ? 'Speichern' : 'Anlegen' }}</span>
          </button>
        </div>
      </v-card>
    </v-dialog>

    <!-- Delete Confirm Dialog -->
    <div v-if="deleteDialog" class="dialog-backdrop" @click="!deleting && (deleteDialog = false)"></div>
    <v-dialog v-model="deleteDialog" max-width="440" :scrim="false" persistent>
      <v-card rounded="xl" class="modern-dialog" elevation="0">
        <button class="close-btn" :disabled="deleting" @click="deleteDialog = false"><v-icon size="18">mdi-close</v-icon></button>
        <div class="dh">
          <div class="dh-icon" style="background: rgba(211,47,47,0.1)"><v-icon size="22" color="#D32F2F">mdi-delete-alert-outline</v-icon></div>
          <div>
            <div class="dh-title">Shop löschen?</div>
            <div class="dh-sub">Diese Aktion kann nicht rückgängig gemacht werden</div>
          </div>
        </div>
        <div class="db">
          <p class="dialog-text">
            Möchtest du den Shop
            <strong class="text-dark">{{ deleteTarget?.name }}</strong>
            wirklich löschen?
          </p>
        </div>
        <div class="dff">
          <button class="bo" :disabled="deleting" @click="deleteDialog = false">Abbrechen</button>
          <button class="bp bp-danger" :disabled="deleting" @click="executeDelete">
            <v-progress-circular v-if="deleting" indeterminate size="14" width="2" color="white" />
            <v-icon v-else size="15">mdi-delete-outline</v-icon>
            <span>Löschen</span>
          </button>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'SettingsOverview',

  data() {
    return {
      focusField: null,

      shops: [],
      loadingShops: false,
      shopDialog: false,
      editShop: null,
      shopForm: { name: '', ablyftProjectId: '' },
      savingShop: false,
      shopMessage: '',
      shopMessageType: 'error',

      deleteDialog: false,
      deleteTarget: null,
      deleting: false,
    };
  },

  mounted() { this.loadShops(); },

  methods: {
    ablyftUrl(projectId) {
      return `https://app.ablyft.com/team/1094/projects/${projectId}/experiments`;
    },

    async loadShops() {
      this.loadingShops = true;
      try {
        const res = await axios.get('/api/reviews/shops');
        this.shops = res.data.data || [];
      } catch (e) {
        console.error('Fehler beim Laden der Shops', e);
      } finally {
        this.loadingShops = false;
      }
    },

    openShopDialog(shop = null) {
      this.editShop = shop;
      this.shopForm = shop
        ? { name: shop.name, ablyftProjectId: shop.ablyftProjectId || '' }
        : { name: '', ablyftProjectId: '' };
      this.shopMessage = '';
      this.shopDialog = true;
    },

    async saveShop() {
      if (!this.shopForm.name.trim()) {
        this.shopMessage = 'Shop-Name ist erforderlich';
        this.shopMessageType = 'error';
        return;
      }
      this.savingShop = true;
      this.shopMessage = '';
      try {
        if (this.editShop) {
          await axios.post('/api/reviews/shops/update', {
            id: this.editShop.id,
            name: this.shopForm.name,
            ablyftProjectId: this.shopForm.ablyftProjectId,
          });
        } else {
          await axios.post('/api/reviews/shops/create', {
            name: this.shopForm.name,
            ablyftProjectId: this.shopForm.ablyftProjectId,
          });
        }
        this.shopDialog = false;
        await this.loadShops();
      } catch (e) {
        this.shopMessage = e.response?.data?.message || 'Fehler beim Speichern';
        this.shopMessageType = 'error';
      } finally {
        this.savingShop = false;
      }
    },

    confirmDeleteShop(shop) {
      this.deleteTarget = shop;
      this.deleteDialog = true;
    },

    async executeDelete() {
      this.deleting = true;
      try {
        await axios.post('/api/reviews/shops/delete', { id: this.deleteTarget.id });
        await this.loadShops();
        this.deleteDialog = false;
      } catch (e) {
        console.error('Fehler beim Löschen', e);
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 26px; font-weight: 800; color: #0a1628; letter-spacing: -0.3px; }
.page-subtitle { font-size: 13px; color: #888; }

/* Section header */
.section-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px; flex-wrap: wrap;
}
.section-icon {
  width: 32px; height: 32px; border-radius: 9px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(91,91,214,0.08);
}
.section-title { font-size: 16px; font-weight: 700; color: #0a1628; }
.section-desc { font-size: 13px; color: #888; }

/* Buttons */
.primary-btn {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 10px 18px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; font-family: inherit;
}
.primary-btn:hover:not(:disabled) { background: #1d3457; }

.action-btn {
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: 1px solid transparent; border-radius: 8px;
  cursor: pointer; color: #666; transition: all 0.15s;
}
.action-btn:hover { background: #f0f2f5; color: #0a1628; }
.action-danger:hover { background: rgba(211,47,47,0.08); color: #D32F2F; }

/* Shops Grid */
.shops-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 14px;
}
.shop-card {
  background: white; border: 1px solid #eceef2; border-radius: 14px;
  padding: 18px; transition: border-color 0.15s;
}
.shop-card:hover { border-color: #c5cbd3; }

.shop-head { display: flex; align-items: center; gap: 12px; margin-bottom: 12px; }
.shop-icon-badge {
  width: 36px; height: 36px; min-width: 36px; border-radius: 9px;
  background: #f0f2f5; display: flex; align-items: center; justify-content: center;
}
.shop-name-wrap { flex: 1; min-width: 0; }
.shop-name {
  font-size: 14px; font-weight: 700; color: #0a1628;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.shop-actions { display: flex; gap: 2px; }

.shop-body {
  padding-top: 12px;
  border-top: 1px solid #f5f5f5;
}
.shop-meta {
  display: flex; align-items: center; gap: 6px;
  font-size: 12px; color: #666;
}
.shop-meta-empty { color: #aaa; }
.meta-label { font-weight: 500; }
.meta-link {
  display: inline-flex; align-items: center; gap: 4px;
  color: #5B5BD6; text-decoration: none; font-weight: 600;
  font-family: 'SFMono-Regular', Menlo, monospace;
}
.meta-link:hover { text-decoration: underline; }

/* Empty */
.empty-state {
  background: white; border: 1px solid #eceef2; border-radius: 16px;
  padding: 64px 32px; text-align: center;
}
.empty-title { font-size: 16px; font-weight: 700; color: #0a1628; margin-bottom: 6px; }
.empty-desc { font-size: 13px; color: #888; }

/* Skeleton */
.skel-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 14px; }
.skel-card {
  height: 120px;
  background: linear-gradient(90deg, #eceef2 25%, #f5f6f8 50%, #eceef2 75%);
  background-size: 200% 100%; animation: shimmer 1.5s infinite; border-radius: 14px;
}
@keyframes shimmer { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

/* Dialog (floowseo pattern) */
.dialog-backdrop { position: fixed; inset: 0; background: rgba(10,22,40,0.5); z-index: 2000; animation: fadeBg 0.2s ease; }
@keyframes fadeBg { from { opacity: 0; } to { opacity: 1; } }

.modern-dialog {
  background: white !important;
  box-shadow: 0 24px 64px rgba(10,22,40,0.18) !important;
  z-index: 2001 !important; position: relative;
  animation: slideUpDlg 0.3s cubic-bezier(0.16,1,0.3,1);
  overflow: visible !important;
}
@keyframes slideUpDlg { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }

.close-btn {
  position: absolute; top: 16px; right: 16px; z-index: 10;
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  background: transparent; border: none; border-radius: 8px; cursor: pointer; color: #888;
  transition: all 0.2s;
}
.close-btn:hover { background: #f0f2f5; color: #0a1628; transform: rotate(90deg); }
.close-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.dh { display: flex; align-items: center; gap: 14px; padding: 28px 28px 0; }
.dh-icon {
  width: 44px; height: 44px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  background: rgba(10,22,40,0.06); flex-shrink: 0;
}
.dh-title { font-size: 18px; font-weight: 700; color: #0a1628; }
.dh-sub { font-size: 12px; color: #999; margin-top: 2px; }
.db { padding: 24px 28px; }
.dff {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 4px 28px 24px;
}
.dialog-text { font-size: 14px; color: #555; line-height: 1.6; }
.text-dark { color: #0a1628; font-weight: 700; }

/* Form inputs (inside dialog) */
.fg { display: flex; flex-direction: column; }
.fl { font-size: 11px; font-weight: 600; color: #999; margin-bottom: 6px; letter-spacing: 0.3px; }
.iw {
  display: flex; align-items: center; gap: 10px;
  height: 42px; padding: 0 14px;
  background: #f8f9fb; border: 1px solid #eceef2; border-radius: 10px;
  transition: all 0.15s;
}
.iw.iw-f { border-color: #0a1628; background: white; box-shadow: 0 0 0 3px rgba(10,22,40,0.04); }
.ii {
  flex: 1; border: none; outline: none; background: transparent;
  font-size: 14px; color: #0a1628; font-weight: 500; min-width: 0;
  font-family: inherit; height: 40px;
}
.ii::placeholder { color: #bbb; font-weight: 400; }
.ii:disabled { opacity: 0.6; }

.hint {
  display: flex; align-items: center; gap: 4px;
  font-size: 11px; color: #888;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.hint a {
  color: #5B5BD6; text-decoration: none;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.hint a:hover { text-decoration: underline; }

/* Dialog buttons */
.bo {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 10px 18px; border: 1px solid #eceef2; border-radius: 10px;
  background: white; font-size: 13px; font-weight: 600; color: #555;
  cursor: pointer; transition: border-color 0.15s; font-family: inherit;
}
.bo:hover:not(:disabled) { border-color: #0a1628; }
.bo:disabled { opacity: 0.5; cursor: not-allowed; }

.bp {
  display: inline-flex; align-items: center; gap: 6px; min-width: 120px;
  padding: 10px 20px; background: #0a1628; color: white;
  border: none; border-radius: 10px; font-size: 13px; font-weight: 700;
  cursor: pointer; transition: background 0.15s; justify-content: center;
  font-family: inherit;
}
.bp:hover:not(:disabled) { background: #1d3457; }
.bp:disabled { opacity: 0.5; cursor: not-allowed; }
.bp-success { background: #2E7D32; }
.bp-success:hover:not(:disabled) { background: #1B5E20; }
.bp-danger { background: #D32F2F; }
.bp-danger:hover:not(:disabled) { background: #B71C1C; }

.alert-error {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(211,47,47,0.08); color: #D32F2F;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}
.alert-success {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 14px; background: rgba(46,125,50,0.08); color: #2E7D32;
  border-radius: 10px; font-size: 13px; font-weight: 500;
}
</style>
